import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";

export { RefreshAtEnd } from "./refresh.at.end.jsx";
export { InfiniteContainer, InfiniteElement } from "./container.jsx";
export { InfiniteScrollContainer, INFINITE_SCROLL_AXIS } from "./infinite.jsx";

export const SCROLLBAR_DIRECTION = {
  UP: "up",
  DOWN: "down",
  BOTH: "both"
};

function InfiniteScrollComponent(props) {
  const {
    children,
    isLoading,
    loaderRenderer = () => (
      <div style={{ height: `${props.endOffset}px` }}>
        <Trans>Loading...</Trans>
      </div>
    ),
    className = "",
    style,
    onEndReached,
    scrollRef,
    onScroll,
    endOffset = 0,
    direction = SCROLLBAR_DIRECTION.DOWN,
    canTriggerOnEnd
  } = props;

  const [lastScrollheight, setLastScrollheight] = useState(0);
  const [isDirectionUp, setIsDirectionUp] = useState(false);
  const currentScrollRef = useRef(null);

  const checkScroll = useCallback(
    function checkScrollCallback(scrollDirection) {
      if (typeof canTriggerOnEnd === "boolean" && canTriggerOnEnd) return false;
      else if (
        typeof canTriggerOnEnd === "object" &&
        canTriggerOnEnd[scrollDirection]
      )
        return false;
      return true;
    },
    [canTriggerOnEnd]
  );

  useImperativeHandle(scrollRef, () => {
    return {
      scrollToBottom: () => {
        currentScrollRef.current.scrollTop =
          currentScrollRef.current.scrollHeight;
      },
      scrollToTop: () => {
        currentScrollRef.current.scrollTop = 0;
      },
      scrollTo: (options) => {
        currentScrollRef.current.scrollTo(options);
      }
    };
  });

  const handleScroll = useCallback(
    function scrollConditionUpdatingEvent(e) {
      if (isLoading) {
        return;
      }
      const sRef = currentScrollRef && currentScrollRef.current;
      if (sRef) {
        setTimeout(() => {
          let scrollTop = sRef.scrollTop;
          let height = sRef.scrollHeight - sRef.clientHeight;
          const y = scrollTop / height;
          if (direction === SCROLLBAR_DIRECTION.BOTH) {
            if (checkScroll(SCROLLBAR_DIRECTION.UP) && y === 0)
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.UP);
            if (checkScroll(SCROLLBAR_DIRECTION.DOWN) && y >= 0.9)
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.DOWN);
            setIsDirectionUp(y === 0);
            onScroll && onScroll(e);
          } else if (direction === SCROLLBAR_DIRECTION.UP && y === 0) {
            if (checkScroll(SCROLLBAR_DIRECTION.UP))
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.UP);
            setIsDirectionUp(true);
          } else if (direction === SCROLLBAR_DIRECTION.DOWN && y >= 0.9) {
            if (checkScroll(SCROLLBAR_DIRECTION.DOWN))
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.DOWN);
          } else {
            onScroll && onScroll(sRef);
          }
        });
      }
    },
    [isLoading, direction, checkScroll, onEndReached, onScroll]
  );

  useEffect(
    function scrollDirectionUpdatingEvent() {
      if (currentScrollRef.current) {
        if (scrollRef) {
          scrollRef.current = currentScrollRef.current;
        }
        if (direction === SCROLLBAR_DIRECTION.UP) {
          currentScrollRef.current.scrollTop =
            currentScrollRef.current.scrollHeight;
        }
      }
    },
    [currentScrollRef, scrollRef, direction]
  );

  useEffect(
    function scrollHeightUpdatingEvent() {
      if (!checkScroll(SCROLLBAR_DIRECTION.UP)) return;
      if (!currentScrollRef.current) return;
      let timerId = null;
      if (isDirectionUp) {
        if (!isLoading) {
          timerId = setTimeout(() => {
            currentScrollRef.current.scrollTop =
              currentScrollRef.current.scrollHeight -
              lastScrollheight -
              endOffset;
          }, 100);
        } else {
          setLastScrollheight(currentScrollRef.current.scrollHeight);
        }
      } else {
        currentScrollRef.current.scroll({ y: `+=${1}` });
      }
      return () => {
        if (timerId) {
          clearTimeout(timerId);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, isDirectionUp]
  );

  return (
    <div
      className={className}
      ref={currentScrollRef}
      onScroll={handleScroll}
      style={style}
    >
      {isDirectionUp && isLoading && loaderRenderer && loaderRenderer()}
      {children}
      {!isDirectionUp && isLoading && loaderRenderer && loaderRenderer()}
    </div>
  );
}

/** This component is used to achieve infinite scrolling natively. */
export const InfiniteScroll = forwardRef((props, ref) => {
  return <InfiniteScrollComponent scrollRef={ref} {...props} />;
});

InfiniteScroll.propTypes = {
  /** A children node that is been render the component */
  children: PropTypes.node.isRequired,
  /** An boolean state which will be used to show the loaderRenderer  */
  isLoading: PropTypes.bool.isRequired,
  /** Custom node which will be used as a fallback if the loader state is true */
  loaderRenderer: PropTypes.func,
  /** Custom callback will be fired if the user reached the end/top */
  onEndReached: PropTypes.func.isRequired,
  /** Event will be fired when user scrolls inside the container */
  onScroll: PropTypes.func,
  /** A class string will be passed */
  className: PropTypes.string,
  /** A ref of the element */
  scrollRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  /** It's refers to the direction in which scroll happens */
  direction: PropTypes.string,
  /** It's either a boolean when the direction is up/down or an object if the direction is both, actually to tell whether to call the onEndReached after a scrollend */
  canTriggerOnEnd: PropTypes.oneOfType([
    PropTypes.bool
    // PropTypes.oneOf(Object.values(SCROLLBAR_DIRECTION))
  ])
};

InfiniteScroll.defaultProps = {
  loaderRenderer: () => (
    <div>
      <Trans>Loading...</Trans>
    </div>
  ),
  className: "",
  endOffset: 0,
  direction: "down"
};

InfiniteScrollComponent.propTypes = InfiniteScroll.propTypes;
InfiniteScrollComponent.defaultProps = InfiniteScroll.defaultPropTypes;
